import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../services/api';
import SearchableSelect from './SearchableSelect';
import '../styles/forms.css';
import '../styles/ManageCategories.css';
import Icon from '@mdi/react';
import { mdiPlus, mdiSquareEditOutline, mdiDeleteOutline, mdiChevronDown, mdiChevronRight } from '@mdi/js';

const emptyForm = () => ({
    name: '',
    parent_id: null,
});

const ManageCategories = () => {
    const [categories, setCategories] = useState([]);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState(emptyForm());
    const [panelOpen, setPanelOpen] = useState(false);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('');
    const [collapsed, setCollapsed] = useState({});

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await api.get('/categories');
            setCategories(response.data);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError('Failed to load categories.');
        }
    };

    const openCreatePanel = (parentId = null) => {
        setEditing(null);
        setFormData({ ...emptyForm(), parent_id: parentId });
        setError('');
        setPanelOpen(true);
    };

    const openEditPanel = (category) => {
        setEditing(category);
        setFormData({
            name: category.name,
            parent_id: category.parent_id,
        });
        setError('');
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setEditing(null);
        setError('');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleParentSelect = (option) => {
        setFormData(prev => ({ ...prev, parent_id: option ? option.value : null }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.name.trim()) {
            setError('Category name is required.');
            return;
        }

        try {
            if (editing) {
                await api.patch(`/categories/${editing.id}`, formData);
            } else {
                await api.post('/categories', formData);
            }
            closePanel();
            await fetchCategories();
        } catch (err) {
            setError('Failed to save category: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleDelete = async (category) => {
        const count = category.transaction_count || 0;
        if (count > 0) {
            window.alert(
                `Cannot delete "${category.name}": it has ${count} transaction(s). ` +
                `Reassign or delete those transactions first.`
            );
            return;
        }
        if (!window.confirm(`Are you sure you want to delete "${category.name}"?`)) {
            return;
        }
        try {
            await api.delete(`/categories/${category.id}`);
            await fetchCategories();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to delete category.';
            window.alert(msg);
            console.error('Error deleting category:', err);
        }
    };

    const toggleCollapse = (id) => {
        setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Build the tree from the flat list.
    const tree = useMemo(() => {
        const byId = {};
        categories.forEach(c => { byId[c.id] = { ...c, children: [] }; });
        const roots = [];
        byId && Object.values(byId).forEach(node => {
            if (node.parent_id && byId[node.parent_id]) {
                byId[node.parent_id].children.push(node);
            } else {
                roots.push(node);
            }
        });
        const sortRec = (nodes) => {
            nodes.sort((a, b) => a.name.localeCompare(b.name));
            nodes.forEach(n => sortRec(n.children));
        };
        sortRec(roots);
        return roots;
    }, [categories]);

    // When filtering, expand all nodes so matches are visible.
    const filteredTree = useMemo(() => {
        if (!filter.trim()) return tree;
        const q = filter.toLowerCase();
        const filterRec = (nodes) => {
            return nodes
                .map(n => {
                    const children = filterRec(n.children);
                    const matches = n.name.toLowerCase().includes(q);
                    if (matches || children.length) {
                        return { ...n, children };
                    }
                    return null;
                })
                .filter(Boolean);
        };
        return filterRec(tree);
    }, [tree, filter]);

    // Compute matching ids to force-expand when filtering.
    const matchIds = useMemo(() => {
        if (!filter.trim()) return new Set();
        const q = filter.toLowerCase();
        const ids = new Set();
        categories.forEach(c => {
            if (c.name.toLowerCase().includes(q)) {
                // add all ancestors
                let cur = c;
                while (cur) {
                    ids.add(cur.id);
                    cur = categories.find(x => x.id === cur.parent_id);
                }
            }
        });
        return ids;
    }, [categories, filter]);

    // Parent options: all categories except the one being edited and its descendants.
    const parentOptions = useMemo(() => {
        const collectDescendants = (id) => {
            const ids = new Set([id]);
            let queue = [id];
            while (queue.length) {
                const cur = queue.pop();
                categories.forEach(c => {
                    if (c.parent_id === cur && !ids.has(c.id)) {
                        ids.add(c.id);
                        queue.push(c.id);
                    }
                });
            }
            return ids;
        };
        const excluded = editing ? collectDescendants(editing.id) : new Set();
        return categories
            .filter(c => !excluded.has(c.id))
            .map(c => ({ value: c.id, label: c.full_name || c.name }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [categories, editing]);

    const renderTree = (nodes, depth = 0) => {
        return nodes.map(node => {
            const hasChildren = node.children.length > 0;
            const isCollapsed = filter.trim() ? false : collapsed[node.id];
            const count = node.transaction_count || 0;
            const blocked = count > 0 || hasChildren;
            return (
                <li key={node.id} className="category-node">
                    <div className="category-row" style={{ paddingLeft: `${depth * 28 + 8}px` }}>
                        {hasChildren ? (
                            <button
                                className="collapse-toggle"
                                onClick={() => toggleCollapse(node.id)}
                                title={isCollapsed ? 'Expand' : 'Collapse'}
                            >
                                <Icon path={isCollapsed ? mdiChevronRight : mdiChevronDown} size={1} />
                            </button>
                        ) : (
                            <span className="collapse-placeholder" />
                        )}
                        <span className="category-name">{node.name}</span>
                        <span className="category-count" title="Transactions">
                            {count > 0 ? count : ''}
                        </span>
                        <span className="category-actions">
                            <button
                                className="btn btn--ghost"
                                onClick={() => openCreatePanel(node.id)}
                                title="Add sub-category"
                            >
                                <Icon path={mdiPlus} size={0.7} />
                            </button>
                            <button
                                className="btn btn--ghost"
                                onClick={() => openEditPanel(node)}
                                title="Edit"
                            >
                                <Icon path={mdiSquareEditOutline} size={0.75} />
                            </button>
                            <button
                                className="btn btn--ghost"
                                onClick={() => handleDelete(node)}
                                title={blocked ? 'Cannot delete: has transactions or sub-categories' : 'Delete'}
                                disabled={blocked}
                            >
                                <Icon path={mdiDeleteOutline} size={0.75} />
                            </button>
                        </span>
                    </div>
                    {hasChildren && !isCollapsed && (
                        <ul className="category-children">{renderTree(node.children, depth + 1)}</ul>
                    )}
                </li>
            );
        });
    };

    const panelTitle = editing ? 'Edit Category' : 'New Category';

    return (
        <div className="card">
            <div className="transaction-table categories-table">
                <h2 className="page-title">Manage Categories</h2>

                <div className="header">
                    <div className="filter-group categories-filter">
                        <input
                            type="text"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter by name…"
                            className="filter-input"
                        />
                    </div>
                    <div className="buttons-bar">
                        <button
                            className="btn btn--primary"
                            onClick={() => openCreatePanel(null)}
                            type="button"
                        >
                            <Icon path={mdiPlus} size={0.8} /> New Category
                        </button>
                    </div>
                </div>

                <div className="table-wrapper categories-tree-wrapper">
                    {filteredTree.length === 0 ? (
                        <div className="empty-row">
                            {filter
                                ? `No categories match "${filter}".`
                                : 'No categories yet. Click "New Category" to create one.'}
                        </div>
                    ) : (
                        <ul className="category-tree-root">{renderTree(filteredTree)}</ul>
                    )}
                </div>

                {panelOpen && (
                    <div className="edit-panel create-panel">
                        <div className="panel-header">
                            <h3>{panelTitle}</h3>
                            <button onClick={closePanel}>×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="form">
                            {error && <div className="error">{error}</div>}

                            <div className="form-group">
                                <label>Name
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter category name"
                                        required
                                    />
                                </label>
                            </div>

                            <div className="form-group">
                                <label>Parent Category
                                    <SearchableSelect
                                        options={parentOptions}
                                        onSelect={handleParentSelect}
                                        value={formData.parent_id}
                                        placeholder="None (top-level)"
                                    />
                                </label>
                            </div>

                            <div className="form-buttons">
                                <button type="button" className="btn btn--secondary" onClick={closePanel}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn--primary">
                                    {editing ? 'Save Changes' : 'Create Category'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageCategories;